import { useEffect, useState } from "react";
import { Bell, BellOff, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * LiveBellButton — a tiny "Notify me when @handle goes live" toggle.
 * Mounted inside the AvatarLightbox so any peek at a creator's face can
 * be turned into a returning viewer with one tap. Backed by the
 * `/api/users/{handle}/live-notify` endpoints.
 *
 * Hides itself when:
 *   • The viewer isn't logged in.
 *   • The lightbox belongs to the viewer's own profile.
 *   • The handle prop is missing (nothing to subscribe to).
 */
export default function LiveBellButton({ handle }) {
  const { user } = useAuth();
  const [subscribed, setSubscribed] = useState(null); // null = unknown, true/false = known
  const [busy, setBusy] = useState(false);

  const isSelf = !!user && !!handle && (user.id === handle || (user.handle || "").toLowerCase() === handle.toLowerCase());

  useEffect(() => {
    if (!user || !handle || isSelf) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/${handle}/live-notify`);
        if (!cancelled) setSubscribed(!!data.subscribed);
      } catch { /* silent — button just doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [user, handle, isSelf]);

  if (!user || !handle || isSelf || subscribed === null) return null;

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !subscribed;
    try {
      if (next) {
        await api.post(`/users/${handle}/live-notify`);
        toast.success(`You'll be notified when @${handle} goes live.`);
      } else {
        await api.delete(`/users/${handle}/live-notify`);
        toast.success(`Live notifications off for @${handle}.`);
      }
      setSubscribed(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update notifications.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid="live-bell-toggle"
      aria-pressed={subscribed}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all disabled:opacity-60 ${
        subscribed
          ? "bg-rose-600 hover:bg-rose-500 border-rose-500 text-white"
          : "bg-white/5 hover:bg-white/10 border-white/20 text-white"
      }`}
    >
      {subscribed ? <Bell size={16} className="fill-current" /> : <Radio size={16} />}
      <span data-testid="live-bell-label">
        {subscribed ? "You'll get a live ping" : `Notify me when @${handle} goes live`}
      </span>
    </button>
  );
}
